import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  TextField,
  InputAdornment,
  IconButton,
  Button,
  Menu,
  MenuItem,
  Divider,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
  ListItemIcon,
  CircularProgress,
  Tooltip,
  Fab,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  useTheme
} from '@mui/material';
import {
  FilterList as FilterIcon,
  Add as AddIcon,
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  Receipt as ReceiptIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Close as CloseIcon,
  AccountBalance as AccountBalanceIcon,
  CalendarMonth as CalendarIcon,
  Person as PersonIcon,
  AttachMoney as MoneyIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getGLTransactions } from '../firebase/glTransactionServices';
import { useSnackbar } from 'notistack';
import { format } from 'date-fns';
import { Invoice, InvoiceStatus, getInvoices, deleteInvoice, subscribeToInvoices } from '../firebase/invoiceServices';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';

// Status options for filtering
const statusOptions: InvoiceStatus[] = [
  'draft',
  'sent',
  'paid',
  'overdue',
  'cancelled'
];

const Invoices: React.FC = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();

  // State for invoices data
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // State for filtering and pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [statusFilter, setStatusFilter] = useState('All Statuses');
  const [customerFilter, setCustomerFilter] = useState('All Customers');

  // State for menu and dialogs
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [filterAnchorEl, setFilterAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteInProgress, setDeleteInProgress] = useState(false);
  const [glTransactionViewOpen, setGLTransactionViewOpen] = useState(false);
  const [selectedInvoiceTitle, setSelectedInvoiceTitle] = useState<string>('');

  const [glDialogOpen, setGlDialogOpen] = useState(false);
  const [glTransactions, setGlTransactions] = useState<any[]>([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);

  // Fetch invoices data
  useEffect(() => {
    setLoading(true);
    setError(null);

    // Subscribe to real-time invoice updates
    const unsubscribe = subscribeToInvoices(
      (invoicesData) => {
        setInvoices(invoicesData);
        setLoading(false);
      },
      startDate,
      endDate
    );

    // Cleanup subscription on unmount
    return () => {
      unsubscribe();
    };
  }, [startDate, endDate]);

  // Handle errors
  useEffect(() => {
    if (error) {
      enqueueSnackbar(error, { variant: 'error' });
    }
  }, [error, enqueueSnackbar]);

  // Get unique customer names for filtering
  const customerOptions = React.useMemo(() => {
    const uniqueCustomers = new Set<string>();
    invoices.forEach(invoice => {
      if (invoice.customerName) {
        uniqueCustomers.add(invoice.customerName);
      }
    });
    return Array.from(uniqueCustomers).sort();
  }, [invoices]);

  // Handle menu open
  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>, invoiceId: string) => {
    setMenuAnchorEl(event.currentTarget);
    setSelectedInvoiceId(invoiceId);
  };

  // Handle menu close
  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    // Don't reset selectedInvoiceId here as it's needed for delete operation
  };

  // Handle filter menu open
  const handleFilterMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setFilterAnchorEl(event.currentTarget);
  };

  // Handle filter menu close
  const handleFilterMenuClose = () => {
    setFilterAnchorEl(null);
  };

  // Handle edit invoice
  const handleEditInvoice = () => {
    if (selectedInvoiceId) {
      navigate(`/invoices/edit/${selectedInvoiceId}`);
    }
    handleMenuClose();
  };

  // Handle view GL transactions
  const handleViewGLTransactions = async () => {
    if (selectedInvoiceId) {
      const invoice = invoices.find(inv => inv.id === selectedInvoiceId);
      if (invoice) {
        setSelectedInvoiceTitle(`Invoice #${invoice.invoiceNumber} - ${invoice.customerName}`);
        setGLTransactionViewOpen(true);
        setLoadingTransactions(true);
        
        try {
          // Fetch GL transactions for this invoice
          const transactions = await getGLTransactions(null, null, 'invoice', selectedInvoiceId);
          setGlTransactions(transactions);
        } catch (error) {
          console.error('Error fetching GL transactions:', error);
          enqueueSnackbar('Error fetching GL transactions', { variant: 'error' });
        } finally {
          setLoadingTransactions(false);
        }
      }
    }
    handleMenuClose();
  };

  // Handle delete invoice dialog open
  const handleDeleteDialogOpen = () => {
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  // Handle delete invoice dialog close
  const handleDeleteDialogClose = () => {
    setDeleteDialogOpen(false);
    // Only reset the selected invoice ID when explicitly canceling the delete operation
    setSelectedInvoiceId(null);
  };

  // Handle delete invoice
  const handleDeleteInvoice = async () => {
    if (!selectedInvoiceId) {
      console.error('No invoice selected for deletion');
      enqueueSnackbar('No invoice selected for deletion', { variant: 'error' });
      return;
    }

    console.log(`Attempting to delete invoice with ID: ${selectedInvoiceId}`);
    setDeleteInProgress(true);
    
    try {
      // Get the invoice details before deletion for better logging
      const invoiceToDelete = invoices.find(inv => inv.id === selectedInvoiceId);
      console.log(`Deleting invoice: ${invoiceToDelete?.invoiceNumber || selectedInvoiceId}`);
      
      // Call the deleteInvoice function with explicit error handling
      await deleteInvoice(selectedInvoiceId)
        .then(() => {
          console.log('Invoice deletion successful');
          enqueueSnackbar('Invoice deleted successfully', { variant: 'success' });
        })
        .catch((err) => {
          console.error('Detailed error deleting invoice:', err);
          throw err; // Re-throw to be caught by the outer catch
        });
    } catch (error: any) {
      console.error('Error in handleDeleteInvoice:', error);
      // Show more detailed error message
      const errorMessage = error?.message || 'Unknown error';
      setError(`Failed to delete invoice: ${errorMessage}. Please try again.`);
      enqueueSnackbar(`Failed to delete invoice: ${errorMessage}`, { variant: 'error' });
    } finally {
      console.log('Delete operation completed, cleaning up UI state');
      setDeleteInProgress(false);
      setDeleteDialogOpen(false);
      setSelectedInvoiceId(null);
    }
  };

  // Handle search
  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
    setPage(0);
  };

  // Handle status filter change
  const handleStatusFilterChange = (event: SelectChangeEvent<string>) => {
    setStatusFilter(event.target.value);
    setPage(0);
  };

  // Handle customer filter change
  const handleCustomerFilterChange = (event: SelectChangeEvent<string>) => {
    setCustomerFilter(event.target.value);
    setPage(0);
  };

  // Handle date filter change
  const handleDateFilterChange = (startDate: Date | null, endDate: Date | null) => {
    setStartDate(startDate);
    setEndDate(endDate);
    setPage(0);
  };

  // Handle clear filters
  const handleClearFilters = () => {
    setSearchQuery('');
    setStartDate(null);
    setEndDate(null);
    setStatusFilter('All Statuses');
    setCustomerFilter('All Customers');
    setPage(0);
  };

  // Handle page change
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Filter invoices based on search query and filters
  const filteredInvoices = invoices.filter(invoice => {
    // Search query filter
    const searchFields = [
      invoice.invoiceNumber,
      invoice.customerName,
      invoice.customerEmail,
      invoice.notes
    ].filter(Boolean).join(' ').toLowerCase();
    
    const matchesSearch = !searchQuery || searchFields.includes(searchQuery.toLowerCase());
    
    // Status filter
    const matchesStatus = statusFilter === 'All Statuses' || invoice.status === statusFilter.toLowerCase();
    
    // Customer filter
    const matchesCustomer = customerFilter === 'All Customers' || invoice.customerName === customerFilter;
    
    return matchesSearch && matchesStatus && matchesCustomer;
  });

  // Get status chip color based on status
  const getStatusChipColor = (status: string) => {
    switch (status) {
      case 'draft':
        return 'default';
      case 'sent':
        return 'primary';
      case 'paid':
        return 'success';
      case 'overdue':
        return 'error';
      case 'cancelled':
        return 'warning';
      default:
        return 'default';
    }
  };

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  // Format date
  const formatDate = (date: Date | null) => {
    if (!date) return '';
    return format(date, 'MMM dd, yyyy');
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1">
            Invoices
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => navigate('/invoices/new')}
          >
            Create Invoice
          </Button>
        </Box>

        {/* Search and filters */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
            {/* Search field */}
            <TextField
              label="Search Invoices"
              variant="outlined"
              size="small"
              value={searchQuery}
              onChange={handleSearch}
              sx={{ flexGrow: 1, minWidth: '200px' }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
            />

            {/* Date filters */}
            <DatePicker
              label="From Date"
              value={startDate}
              onChange={(date) => handleDateFilterChange(date, endDate)}
              slotProps={{ textField: { size: 'small' } }}
            />
            <DatePicker
              label="To Date"
              value={endDate}
              onChange={(date) => handleDateFilterChange(startDate, date)}
              slotProps={{ textField: { size: 'small' } }}
            />

            {/* Status filter */}
            <FormControl size="small" sx={{ minWidth: '150px' }}>
              <InputLabel id="status-filter-label">Status</InputLabel>
              <Select
                labelId="status-filter-label"
                value={statusFilter}
                label="Status"
                onChange={handleStatusFilterChange}
              >
                <MenuItem value="All Statuses">All Statuses</MenuItem>
                {statusOptions.map((status) => (
                  <MenuItem key={status} value={status}>
                    {status.charAt(0).toUpperCase() + status.slice(1)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Customer filter */}
            <FormControl size="small" sx={{ minWidth: '200px' }}>
              <InputLabel id="customer-filter-label">Customer</InputLabel>
              <Select
                labelId="customer-filter-label"
                value={customerFilter}
                label="Customer"
                onChange={handleCustomerFilterChange}
              >
                <MenuItem value="All Customers">All Customers</MenuItem>
                {customerOptions.map((customer) => (
                  <MenuItem key={customer} value={customer}>
                    {customer}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Clear filters button */}
            <Button
              variant="outlined"
              startIcon={<CloseIcon />}
              onClick={handleClearFilters}
              size="small"
            >
              Clear Filters
            </Button>
          </Box>
        </Paper>

        {/* Invoices table */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper}>
            <Table sx={{ minWidth: 650 }} aria-label="invoices table">
              <TableHead>
                <TableRow>
                  <TableCell>Invoice #</TableCell>
                  <TableCell>Customer</TableCell>
                  <TableCell>Issue Date</TableCell>
                  <TableCell>Due Date</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredInvoices.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography variant="body1" sx={{ py: 2 }}>
                        No invoices found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredInvoices
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((invoice) => (
                      <TableRow key={invoice.id} hover>
                        <TableCell>{invoice.invoiceNumber}</TableCell>
                        <TableCell>{invoice.customerName}</TableCell>
                        <TableCell>{formatDate(invoice.issueDate as Date)}</TableCell>
                        <TableCell>{formatDate(invoice.dueDate as Date)}</TableCell>
                        <TableCell>{formatCurrency(invoice.amount)}</TableCell>
                        <TableCell>
                          <Chip
                            label={invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                            color={getStatusChipColor(invoice.status) as any}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            aria-label="invoice actions"
                            aria-controls="invoice-menu"
                            aria-haspopup="true"
                            onClick={(e) => handleMenuOpen(e, invoice.id || '')}
                          >
                            <MoreVertIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                )}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={filteredInvoices.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </TableContainer>
        )}

        {/* Invoice actions menu */}
        <Menu
          id="invoice-menu"
          anchorEl={menuAnchorEl}
          open={Boolean(menuAnchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={handleEditInvoice}>
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            Edit Invoice
          </MenuItem>
          <MenuItem onClick={handleViewGLTransactions}>
            <ListItemIcon>
              <AccountBalanceIcon fontSize="small" />
            </ListItemIcon>
            View GL Transactions
          </MenuItem>
          <Divider />
          <MenuItem onClick={handleDeleteDialogOpen} sx={{ color: 'error.main' }}>
            <ListItemIcon>
              <DeleteIcon fontSize="small" color="error" />
            </ListItemIcon>
            Delete Invoice
          </MenuItem>
        </Menu>

        {/* Delete confirmation dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={handleDeleteDialogClose}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">
            Delete Invoice
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              Are you sure you want to delete this invoice? This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDeleteDialogClose} color="inherit" variant="outlined">
              Cancel
            </Button>
            <Button 
              onClick={handleDeleteInvoice} 
              color="error" 
              variant="contained" 
              autoFocus
              disabled={deleteInProgress}
            >
              {deleteInProgress ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* GL Transaction view dialog */}
        <Dialog
          open={glTransactionViewOpen}
          onClose={() => setGLTransactionViewOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            GL Transactions - {selectedInvoiceTitle}
            <IconButton
              aria-label="close"
              onClick={() => setGLTransactionViewOpen(false)}
              sx={{ position: 'absolute', right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            {loadingTransactions ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : glTransactions.length === 0 ? (
              <Typography variant="body1" sx={{ py: 2 }}>
                No GL transactions found for this invoice.
              </Typography>
            ) : (
              <Box>
                {glTransactions.map((transaction, index) => (
                  <Paper key={transaction.id} sx={{ p: 2, mb: 2 }}>
                    <Typography variant="h6" gutterBottom>
                      Transaction #{index + 1}: {transaction.description}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Date: {formatDate(transaction.transactionDate)}
                    </Typography>
                    
                    <TableContainer component={Paper} variant="outlined" sx={{ mt: 2 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Account Number</TableCell>
                            <TableCell>Account Name</TableCell>
                            <TableCell align="right">Debit</TableCell>
                            <TableCell align="right">Credit</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {transaction.entries.map((entry: any, entryIndex: number) => (
                            <TableRow key={entryIndex}>
                              <TableCell>{entry.accountNumber}</TableCell>
                              <TableCell>{entry.accountName}</TableCell>
                              <TableCell align="right">
                                {entry.debit > 0 ? formatCurrency(entry.debit) : ''}
                              </TableCell>
                              <TableCell align="right">
                                {entry.credit > 0 ? formatCurrency(entry.credit) : ''}
                              </TableCell>
                            </TableRow>
                          ))}
                          <TableRow sx={{ '& td': { fontWeight: 'bold', borderTop: '1px solid rgba(224, 224, 224, 1)' } }}>
                            <TableCell colSpan={2}>Total</TableCell>
                            <TableCell align="right">
                              {formatCurrency(transaction.entries.reduce((sum: number, entry: any) => sum + entry.debit, 0))}
                            </TableCell>
                            <TableCell align="right">
                              {formatCurrency(transaction.entries.reduce((sum: number, entry: any) => sum + entry.credit, 0))}
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                    
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" color="primary">
                        Double-Entry Accounting Explanation:
                      </Typography>
                      <Typography variant="body2">
                        This transaction follows double-entry accounting principles. For an invoice:
                      </Typography>
                      <Box component="ul" sx={{ mt: 1, pl: 2 }}>
                        <li>Accounts Receivable is debited (increased) for the total invoice amount</li>
                        <li>Revenue Clearing Account is credited (increased) for the total invoice amount</li>
                        <li>Revenue Clearing Account is debited (decreased) for each line item amount</li>
                        <li>Individual Revenue Accounts are credited (increased) for each line item amount</li>
                      </Box>
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        This ensures proper accounting with the clearing account (6969999) acting as an intermediary.
                      </Typography>
                    </Box>
                  </Paper>
                ))}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setGLTransactionViewOpen(false)}>
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default Invoices;
